import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { OAuthLoginButton } from '@/components/OAuthLoginButton';
import { EphemeralSessionBanner } from '@/components/EphemeralSessionBanner';
import {
  CancelConfirmModal,
  PlanChangeConfirmModal,
} from '@/components/settings/BillingConfirmModals';
import { RenameDialog, DeleteDialog } from '@/components/paper/AgentsPaper';
import { useDevComponentPreview } from '@/lib/devComponentPreview';
import { isPaperThemeActive } from '@/hooks/useThemeFlag';
import { cn } from '@/lib/utils';

const noop = () => {};

const PREVIEW_PLAN = { id: 'pro', name: 'Pro', price: 20 };
const PREVIEW_PERIOD_END = Math.floor(Date.now() / 1000) + 14 * 24 * 60 * 60;

const Preview = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1.5">
    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--fg-3)]">{label}</div>
    {children}
  </div>
);

export const DevComponentPreviewHost = () => {
  const previews = useDevComponentPreview();
  const paper = isPaperThemeActive();
  const toastIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!paper) return;
    if (previews.sonnerToast && toastIdRef.current === null) {
      toastIdRef.current = toast.message('Preview toast', {
        description: 'Sonner styling preview — paper theme.',
        duration: Infinity,
      });
    } else if (!previews.sonnerToast && toastIdRef.current !== null) {
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  }, [paper, previews.sonnerToast]);

  const cornerOn =
    previews.authPending ||
    previews.authSuccess ||
    previews.authError ||
    previews.ephemeralBanner;
  const modalOn =
    previews.billingCancel ||
    previews.billingChange ||
    previews.agentRename ||
    previews.agentDelete;

  if (!paper || (!cornerOn && !modalOn && !previews.sonnerToast)) return null;

  const node = (
    <>
      {cornerOn && (
        <div
          className={cn(
            'fixed bottom-3 right-3 z-[10050] w-[360px] max-w-[calc(100vw-24px)]',
            'rounded-lg border border-solid border-[var(--hair)] bg-[var(--surface)]/95 p-4 shadow-lg backdrop-blur-sm',
            'flex flex-col gap-4',
          )}
          style={{ fontFamily: 'var(--font-sans-paper)' }}
        >
          {previews.authPending && (
            <Preview label="OAuth · pending">
              <OAuthLoginButton
                sessionId="preview"
                provider="google"
                displayName="Google"
                iconUrl=""
                authStartUrl=""
                status="pending"
                onAuthComplete={noop}
              />
            </Preview>
          )}

          {previews.authSuccess && (
            <Preview label="OAuth · success">
              <OAuthLoginButton
                sessionId="preview"
                provider="google"
                displayName="Google"
                iconUrl=""
                authStartUrl=""
                status="success"
                onAuthComplete={noop}
              />
            </Preview>
          )}

          {previews.authError && (
            <Preview label="OAuth · error">
              <OAuthLoginButton
                sessionId="preview"
                provider="google"
                displayName="Google"
                iconUrl=""
                authStartUrl=""
                status="error"
                onAuthComplete={noop}
              />
            </Preview>
          )}

          {previews.ephemeralBanner && (
            <Preview label="Ephemeral session banner">
              <EphemeralSessionBanner visible onLogin={noop} />
            </Preview>
          )}
        </div>
      )}

      {previews.billingCancel && (
        <CancelConfirmModal
          currentPeriodEnd={PREVIEW_PERIOD_END}
          cancelLoading={false}
          onCancel={noop}
          onKeep={noop}
        />
      )}

      {previews.billingChange && (
        <PlanChangeConfirmModal
          plan={PREVIEW_PLAN}
          currentPeriodEnd={PREVIEW_PERIOD_END}
          isPendingDowngrade={false}
          isDowngrading={false}
          isCanceling={false}
          prorationLoading={false}
          prorationPreview={{ immediateChargeUsd: 12.5, currency: 'usd' }}
          onConfirm={noop}
          onCancel={noop}
        />
      )}

      {previews.agentRename && (
        <RenameDialog value="Hello Chat Request" onSave={noop} onClose={noop} />
      )}

      {previews.agentDelete && <DeleteDialog onConfirm={noop} onClose={noop} />}
    </>
  );

  return createPortal(node, document.body);
};
